import { CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import type React from "react";
import type { MeasurementField, ReferenceRange } from "@/types/measurement";
import MeasurementChartTooltip, { type MeasurementChartPayloadItem, type MeasurementChartPointSource } from "./chart-tooltip";
interface CartesianElementsOptions {
  /** For multi-field charts: maps field ID to field metadata */
  fieldMap?: Map<string, MeasurementField>;
  /** For single-field charts: the field display name */
  fieldName?: string;
  /** For single-field charts: the unit string */
  unit?: string | null;
  /** Reference ranges to display in tooltip */
  referenceRanges?: ReferenceRange[];
  /** Font size for axis ticks (default: 11 for patient view, 10 for preview) */
  tickFontSize?: number;
  /** YAxis width (default: 40 for patient view, 36 for preview) */
  yAxisWidth?: number;
  /** Unit string appended to YAxis tick labels */
  yAxisUnit?: string;
  /** Tooltip size variant */
  tooltipSize?: "sm" | "xs";
  /** Tooltip cursor style (set to false to hide hover cursor) */
  cursor?: boolean;
  /** Resolve source metadata for the hovered point/series. */
  getPointSource?: (item: MeasurementChartPayloadItem) => MeasurementChartPointSource | null | undefined;
  /**
   * Turn a row's `date` into display text: `short` for axis ticks, `long` for
   * the tooltip header. Recharts looks up the hovered row by its `date`, so
   * `date` must be unique; a yearless label like "Sep 30" matches last year's
   * row first. Keep `date` a full bucket key and format it here instead.
   */
  formatDate?: (date: string, style: "short" | "long") => string;
}

/**
 * Build shared CartesianGrid + XAxis + YAxis + Tooltip elements for Recharts.
 *
 * Returns an array of ReactNode that must be spread as direct children of a
 * Recharts chart component (LineChart, BarChart, etc.) because Recharts uses
 * React.Children to find elements by type.
 */
export default function cartesianElements(options: CartesianElementsOptions = {}): React.ReactNode[] {
  const {
    fieldMap,
    fieldName,
    unit,
    referenceRanges,
    tickFontSize = 11,
    yAxisWidth = 40,
    yAxisUnit,
    tooltipSize = "sm",
    cursor,
    getPointSource,
    formatDate
  } = options;
  return [<CartesianGrid key="grid" strokeDasharray="3 3" className="stroke-border" vertical={false} />, <XAxis key="xaxis" dataKey="date" tick={{
    fontSize: tickFontSize,
    angle: -45,
    textAnchor: "end",
    className: "fill-foreground-secondary"
  } as Record<string, unknown>} height={50} tickFormatter={formatDate ? value => formatDate(String(value), "short") : undefined} tickLine={false} axisLine={false} />, <YAxis key="yaxis" tick={{
    fontSize: tickFontSize,
    className: "fill-foreground-secondary"
  }} tickLine={false} axisLine={false} width={yAxisWidth} unit={yAxisUnit} domain={["auto", "auto"]} />, <Tooltip key="tooltip" cursor={cursor} content={({
    active,
    payload,
    label
  }) => <MeasurementChartTooltip active={active} payload={payload ? [...payload] : []} label={label === undefined ? undefined : formatDate ? formatDate(String(label), "long") : String(label)} fieldMap={fieldMap} fieldName={fieldName} unit={unit} referenceRanges={referenceRanges} size={tooltipSize} getPointSource={getPointSource} />} />];
}